import { createAsyncThunk } from '@reduxjs/toolkit';
import { format, intervalToDuration } from 'date-fns';
import { nomeSobrenome } from '@utils';

import { APIAutenticacao } from '../../common/services/autenticacao';
import { validaLogin } from '../../common/services/auth.service';
import { ConfiguracaoPortalService } from '../../common/services/configuracaoPortal';
import { DadosPessoaisService } from '../../common/services/dados-pessoais';
import { insereFaleConosco } from '../../common/services/fale-conosco';
import { PlanoParticipanteService } from '../../common/services/planoParticipante';
import { RequerimentoService } from '../../common/services/requerimento';
import {
  resetAllStores,
  resetPlanScopedStores,
  setPlanoAtual,
} from '../sharedActions';
import {
  getMenuAtendimentoVirtual,
  getDataCorte,
  getPersonalizacao,
  getTelasDisponiveis,
} from '../configs/thunks';
import { getDIB, getPatrocinadora } from '../plan/thunks';
import { getAssinaturaPendente } from '../loan/thunks';

const mapDadosSAPNaoTratados = (raw) => {
  return {
    matricula: raw?.PERNR?.[0] ?? '',
    nome: raw?.CNAME?.[0] ?? '',
    matriculaFalecido: raw?.MATRICULA_FALECIDO?.[0] ?? '',
    dependente: raw?.NR_PENS_DEPENDENTE?.[0] ?? '',
    cep: raw?.CEP?.[0] ?? '',
    endereco: raw?.ENDERECO?.[0] ?? '',
    numero: raw?.NUMERO?.[0] ?? '',
    complemento: raw?.COMPLEMENTO?.[0] ?? '',
    bairro: raw?.BAIRRO?.[0] ?? '',
    cidade: raw?.CIDADE?.[0] ?? '',
    estado: raw?.ESTADO?.[0] ?? '',
    telefoneFixo: raw?.TELEFONE_FIXO?.[0] ?? '',
    celular: raw?.CELULAR?.[0] ?? '',
    email: raw?.EMAIL?.[0] ?? '',
    dtNascimento: raw?.DAT_NASC?.[0] ?? '',
    sexo: raw?.SEXO?.[0] ?? '',
    mae: raw?.MAE?.[0] ?? '',
    pai: raw?.PAI?.[0] ?? '',
    conjuge: raw?.CONJUGE?.[0] ?? '',
    cpf: raw?.CPF?.[0] ?? '',
    rg: raw?.RG?.[0] ?? '',
    orgEmissor: raw?.ORG_EMISSOR?.[0] ?? '',
    uf: raw?.UF?.[0] ?? '',
    dtEmissao: raw?.DAT_EMISSAO?.[0] ?? '',
    localNascimento: raw?.LOC_NACIMENTO?.[0] ?? '',
    estadoNasc: raw?.ESTADO_NASC?.[0] ?? '',
    pais: raw?.PAIS?.[0] ?? '',
    nacionalidade: raw?.NACIONALIDADE?.[0] ?? '',
    estadoCivil: raw?.ESTADO_CIVIL?.[0] ?? '',
    numBanco: raw?.NUM_BANCO?.[0] ?? '',
    agencia: raw?.AGENCIA?.[0] ?? '',
    conta: raw?.CONTA?.[0] ?? '',
    status: raw?.STATUS?.[0] ?? '',
    dtAdminissao: raw?.DATA_ADMISSAO?.[0] ?? '',
    dtDesligamento: raw?.DATA_DESLIGAMENTO?.[0] ?? '',
    motivoDeslig: raw?.MOTIVO_DESLIG?.[0] ?? '',
    dtAfastamento: raw?.DATA_AFASTAMENTO?.[0] ?? '',
    motivoEncerramento: raw?.MOTIVO_AFASTAMENTO?.[0] ?? '',
    tpBeneficio: raw?.TIPO_BENEFICIO?.[0] ?? '',
    dtEncerramentoBeneficio: raw?.DT_ENCE_BENEFICIO?.[0] ?? '',
    motivoAfastamento: raw?.MOT_ENCERRAMENTO?.[0] ?? '',
    planoPrevidencia: Array.isArray(raw?.PLANO_PREVIDENCIA?.[0])
      ? raw.PLANO_PREVIDENCIA[0]
      : [],
    situacaoParticipante: raw?.SIT_PART_PLANO?.[0] ?? '',
  };
};

const mapDadosPessoaisSAP = (raw, matricula) => {
  const planosRaw = Array.isArray(raw?.PLANO_PREVIDENCIA?.[0])
    ? raw.PLANO_PREVIDENCIA[0]
    : [];

  const planoPrevidencia = planosRaw.map((element) => ({
    id: element.IdCardeal?.toString() ?? '',
    name: element.descricao ?? '',
    sitPart: element.SitPart ?? '',
    tipoBeneficio: element.TipoBeneficio ?? '',
  }));

  const dataNascimentoRaw = raw?.DAT_NASC?.[0] ?? '';
  const dataNascimento = dataNascimentoRaw
    ? format(new Date(dataNascimentoRaw.replace(/-/g, '/')), 'dd/MM/yyyy')
    : dataNascimentoRaw;

  const dataExpedicaoRgRaw = raw?.DAT_EMISSAO?.[0] ?? '';
  let dataExpedicaoRg = '';
  if (dataExpedicaoRgRaw === '0000-00-00') {
    dataExpedicaoRg = '01/01/9999';
  } else if (dataExpedicaoRgRaw) {
    dataExpedicaoRg = format(
      new Date(dataExpedicaoRgRaw.replace(/-/g, '/')),
      'dd/MM/yyyy'
    );
  }

  const formataNumCelular = () => {
    let formatNumCelular = raw?.CELULAR?.[0]?.replace('-', '') || '';
    if (formatNumCelular[0] === '0') {
      formatNumCelular = formatNumCelular.substring(1);
    }
    return formatNumCelular;
  };
  const celular = formataNumCelular();

  let idade = 0;
  if (dataNascimentoRaw) {
    idade =
      intervalToDuration({
        start: new Date(),
        end: new Date(dataNascimentoRaw),
      }).years || 0;
  }

  const contaBancaria = (raw?.CONTA?.[0] ?? '').split('-');
  const dadosBancoRaw = (raw?.NUM_BANCO?.[0] ?? '').split('-');

  return {
    id: matricula,
    nome: nomeSobrenome(raw?.CNAME?.[0]?.replace(/\.$/, '') || ''),
    email: raw?.EMAIL?.[0] ?? '',
    dataNascimento,
    idade,
    maiorIdade: idade >= 18,
    dataAdmissao: raw?.DATA_ADMISSAO?.[0] ?? '',
    dataExpedicaoRg,
    logradouro: raw?.ENDERECO?.[0] ?? '',
    numero: raw?.NUMERO?.[0] ?? '',
    complemento: raw?.COMPLEMENTO?.[0] ?? '',
    bairro: raw?.BAIRRO?.[0] ?? '',
    cidade: raw?.CIDADE?.[0] ?? '',
    estado: raw?.ESTADO?.[0] ?? '',
    cep: raw?.CEP?.[0] ?? '',
    telefone: raw?.TELEFONE_FIXO?.[0] ?? '',
    celular,
    cpf: raw?.CPF?.[0] ?? '',
    codBanco: (dadosBancoRaw[0] ?? '').trim(),
    agencia: raw?.AGENCIA?.[0] ? String(raw.AGENCIA[0]).trim() : '',
    conta: contaBancaria[0] ? String(contaBancaria[0]).trim() : '',
    digitoConta: contaBancaria[1] ? String(contaBancaria[1]).trim() : '',
    statusPlano: raw?.STATUS?.[0] ?? '',
    sitPartPlano: raw?.SIT_PART_PLANO?.[0] ?? '',
    planoPrevidencia,
  };
};

export const loginADM = createAsyncThunk(
  'user/loginADM',
  async ({ matricula, token }, { rejectWithValue }) => {
    try {
      const response = await APIAutenticacao.validaLoginADM(token, matricula);
      return {
        matricula: response.matricula,
        token: response.token,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message ?? 'Erro ao simular usuário. Tente novamente.'
      );
    }
  }
);

export const login = createAsyncThunk(
  'user/login',
  async ({ matricula, senha }, { rejectWithValue }) => {
    try {
      // To-do: procurar chamada ValidaLogin no backend
      const request = await validaLogin(matricula, senha);

      if (request.success) {
        return { success: true, error: '', token: request.data.token };
      }
      return rejectWithValue(
        request.error || 'Erro inesperado. Tente novamente mais tarde.'
      );
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

export const getPaginasDisponiveisExtrato = createAsyncThunk(
  'user/getPaginasDisponiveisExtrato',
  async ({ plano, statusPlano }, { rejectWithValue }) => {
    try {
      const service = new PlanoParticipanteService();
      const response = await service.obterPaginasPorPlano(plano, statusPlano);
      const pages = response?.data?.Links ?? [];
      const dadosPlano = response?.data?.DadosPlano ?? {
        Plano: '',
        CNPB: '',
      };
      return {
        pages,
        plano: dadosPlano.Plano ?? '',
        cnpb: dadosPlano.CNPB ?? '',
      };
    } catch (error) {
      console.error('Erro ao obter páginas por plano:', error);
      return rejectWithValue(
        error?.message ?? 'Erro ao obter páginas do extrato'
      );
    }
  }
);

export const getDadosPessoais = createAsyncThunk(
  'user/getDadosPessoais',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const { matricula, token } = getState().user.data;
      const service = new DadosPessoaisService();

      const response = await service.getDadosPessoaisSAP(matricula, token);
      const raw = response.data;
      const dadosSAPNaoTratados = mapDadosSAPNaoTratados(raw);
      const data = mapDadosPessoaisSAP(raw, matricula);

      const hasPlanoB = data.planoPrevidencia.find(
        (item) => item.name === 'Plano B'
      );
      const plano = hasPlanoB ||
        data.planoPrevidencia[0] || {
          id: '3',
          tipoBeneficio: '',
          sitPart: '',
        };

      // setPlanoAtual antes dos thunks dependentes para que getPersonalizacao
      // e similares possam ler state.user.data.plano
      dispatch(setPlanoAtual(plano.id));

      dispatch(getPatrocinadora());
      dispatch(getDIB());
      dispatch(getAssinaturaPendente());

      dispatch(
        getTelasDisponiveis({
          plano: plano.id,
          tipoBeneficio: plano.tipoBeneficio,
          sitPart: plano.sitPart,
        })
      );
      dispatch(
        getMenuAtendimentoVirtual({
          plano: plano.id,
          tipoBeneficio: plano.tipoBeneficio,
          sitPart: plano.sitPart,
        })
      );
      dispatch(getDataCorte());
      dispatch(getPersonalizacao());

      dispatch(
        getPaginasDisponiveisExtrato({
          plano: plano.id,
          statusPlano: data.statusPlano ?? '',
        })
      );

      return { data, planoID: plano.id, dadosSAPNaoTratados };
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

export const alteraPlano = createAsyncThunk(
  'user/alteraPlano',
  async (plano, { dispatch, getState }) => {
    dispatch(resetPlanScopedStores());
    dispatch(setPlanoAtual(plano));

    const planosDisponiveis = getState().user.dadosPessoais.planoPrevidencia;

    const filteredPlan =
      planosDisponiveis.find(
        (plan) => plan.id.toString() === plano.toString()
      ) || planosDisponiveis[0];

    dispatch(getPatrocinadora());
    dispatch(getDIB());

    dispatch(getAssinaturaPendente());
    dispatch(
      getTelasDisponiveis({
        plano,
        tipoBeneficio: filteredPlan.tipoBeneficio,
        sitPart: filteredPlan.sitPart,
      })
    );
    dispatch(
      getMenuAtendimentoVirtual({
        plano,
        tipoBeneficio: filteredPlan.tipoBeneficio,
        sitPart: filteredPlan.sitPart,
      })
    );
    dispatch(getDataCorte());
    dispatch(getPersonalizacao());

    return { plano: filteredPlan };
  }
);

export const resetStates = createAsyncThunk(
  'user/resetStates',
  async (_, { dispatch }) => {
    dispatch(resetAllStores());
  }
);

export const getPendenciasUsuario = createAsyncThunk(
  'user/getPendenciasUsuario',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { matricula, token } = getState().user.data;
      const confPortalService = new ConfiguracaoPortalService();
      const pendenciasUsuario =
        await confPortalService.VerificarPendenciasUsuario(matricula, token);

      return pendenciasUsuario;
    } catch (error) {
      return rejectWithValue('Erro inesperado. Tente novamente mais tarde.');
    }
  }
);

const separarCamposAnexosFaleConosco = (campos) => {
  const camposAnexosPrenchidos = Object.keys(campos)
    .filter((k) => k.includes('anexo') && !!campos[k])
    .reduce((acc, k) => [...acc, { name: k, file: campos[k] }], []);
  return { camposAnexosPrenchidos };
};

const enviarAnexosFaleConosco = async ({
  anexos,
  service,
  matricula,
  token,
  protocolo,
}) => {
  if (!Array.isArray(anexos) || anexos.length === 0) return;

  const promises = anexos.flatMap((anexo) => {
    const arquivos = Array.isArray(anexo.file)
      ? anexo.file
      : Array.from(anexo.file || []);

    return arquivos.map((arquivo) =>
      service.persistirAnexo({
        anexo: arquivo,
        protocolo,
        matricula,
        token,
        nomearquivo: arquivo.name,
        tipoanexo: anexo.name.replace('anexo', ''),
      })
    );
  });

  await Promise.all(promises);
};

export const enviarFaleConosco = createAsyncThunk(
  'user/enviarFaleConosco',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { data } = state.user || {};
      const { matricula, token } = data || {};

      if (!matricula || !token) {
        return rejectWithValue('Usuário não autenticado.');
      }

      const {
        assunto,
        celular,
        eMail,
        mensagem,
        nome,
        telefone,
        anexoFaleConosco,
      } = payload;

      const matriculaStr = String(matricula);
      let protocolo = '';

      if (anexoFaleConosco) {
        const service = new RequerimentoService();
        const protocoloResponse = await service.gerarProtocoloAtendimento(
          matriculaStr,
          token
        );
        protocolo =
          protocoloResponse?.data?.[0]?.protocolo ??
          protocoloResponse?.data?.[0]?.Protocolo ??
          '';

        const { camposAnexosPrenchidos } =
          separarCamposAnexosFaleConosco(payload);
        await enviarAnexosFaleConosco({
          anexos: camposAnexosPrenchidos,
          service,
          matricula: matriculaStr,
          token,
          protocolo,
        });
      }

      const response = await insereFaleConosco({
        Assunto: assunto,
        Celular: celular,
        Email: eMail,
        Matricula: matriculaStr,
        Mensagem: mensagem,
        Nome: nome,
        Telefone: telefone,
        Protocolo: protocolo,
        token,
      });

      if (!response?.data?.[0] || response.data[0].linhasafetadas <= 0) {
        return rejectWithValue('Erro ao enviar fale conosco.');
      }

      return { success: true };
    } catch (error) {
      return rejectWithValue('Erro no envio do fale conosco.');
    }
  }
);

export const enviarSimulacaoEmprestimo = createAsyncThunk(
  'user/enviarSimulacaoEmprestimo',
  async ({ campos, anexos }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { data } = state.user || {};
      const { matricula, token } = data || {};

      if (!matricula || !token) {
        return rejectWithValue('Usuário não autenticado.');
      }

      const service = new RequerimentoService();
      const matriculaStr = String(matricula);
      const formulario = 'SimulacaoEmprestimo';

      const protocoloResponse = await service.gerarProtocoloAtendimento(
        matriculaStr,
        token
      );

      const protocolo =
        protocoloResponse?.data?.[0]?.protocolo ??
        protocoloResponse?.data?.[0]?.Protocolo ??
        '';

      const responseForm = await service.persistirFormulario(
        matriculaStr,
        token,
        campos,
        formulario,
        protocolo
      );

      if (
        responseForm.status !== 200 ||
        !responseForm.data?.[0] ||
        !responseForm.data[0].linhasafetadas
      ) {
        return rejectWithValue('Erro ao enviar solicitação de empréstimo.');
      }

      if (Array.isArray(anexos) && anexos.length > 0) {
        const uploadPromises = anexos.map((arquivo) =>
          service.persistirAnexo({
            anexo: arquivo,
            protocolo,
            matricula: matriculaStr,
            token,
            nomearquivo: arquivo.name,
            tipoanexo: 'Emprestimo',
          })
        );

        await Promise.all(uploadPromises);
      }

      return { success: true };
    } catch (error) {
      return rejectWithValue('Erro ao enviar solicitação de empréstimo.');
    }
  }
);
